import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Cpu,
  Server,
  HardDrive,
  Database,
  Zap,
  Loader2,
  Check
} from 'lucide-react';

const MonitoringThresholds = ({
  thresholds,
  savingThresholds,
  onSaveThresholds,
  updateThreshold
}) => {
  if (!thresholds) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Alert Thresholds</h3>
            <p className="text-sm text-gray-600">Configure when alerts should be triggered</p>
          </div>
          <Button
            onClick={onSaveThresholds}
            disabled={savingThresholds}
            className="bg-pink-600 hover:bg-pink-700"
          >
            {savingThresholds ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CPU Thresholds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium">CPU Usage</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Warning (%)</label>
                <Input
                  type="number"
                  value={thresholds.cpu_warning || ''}
                  onChange={(e) => updateThreshold('cpu_warning', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Critical (%)</label>
                <Input
                  type="number"
                  value={thresholds.cpu_critical || ''}
                  onChange={(e) => updateThreshold('cpu_critical', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Memory Thresholds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-purple-600" />
              <h4 className="font-medium">Memory Usage</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Warning (%)</label>
                <Input
                  type="number"
                  value={thresholds.memory_warning || ''}
                  onChange={(e) => updateThreshold('memory_warning', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Critical (%)</label>
                <Input
                  type="number"
                  value={thresholds.memory_critical || ''}
                  onChange={(e) => updateThreshold('memory_critical', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Disk Thresholds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-orange-600" />
              <h4 className="font-medium">Disk Usage</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Warning (%)</label>
                <Input
                  type="number"
                  value={thresholds.disk_warning || ''}
                  onChange={(e) => updateThreshold('disk_warning', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Critical (%)</label>
                <Input
                  type="number"
                  value={thresholds.disk_critical || ''}
                  onChange={(e) => updateThreshold('disk_critical', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* DB Connection Thresholds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-green-600" />
              <h4 className="font-medium">DB Connections (max 200)</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Warning</label>
                <Input
                  type="number"
                  value={thresholds.db_connections_warning || ''}
                  onChange={(e) => updateThreshold('db_connections_warning', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Critical</label>
                <Input
                  type="number"
                  value={thresholds.db_connections_critical || ''}
                  onChange={(e) => updateThreshold('db_connections_critical', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Cache Hit Rate Thresholds */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-600" />
              <h4 className="font-medium">Cache Hit Rate (alert if below)</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Warning (%)</label>
                <Input
                  type="number"
                  value={thresholds.cache_hit_warning || ''}
                  onChange={(e) => updateThreshold('cache_hit_warning', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Critical (%)</label>
                <Input
                  type="number"
                  value={thresholds.cache_hit_critical || ''}
                  onChange={(e) => updateThreshold('cache_hit_critical', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Alerts are checked automatically every 2 minutes. 
            Critical alerts will also send email notifications to all admins.
            There's a 5-minute cooldown between alerts of the same type to prevent notification spam.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default MonitoringThresholds;
